import React from 'react';
import { Box, Text, Anchor } from '@mantine/core';
import { IconShoppingCart, IconCheck, IconQuestionMark, IconLink } from '@tabler/icons-react';
import { StepProps, WEBHOOK_GROUPS, WEBHOOK_INDEX, CUSTOM_INTEGRATION } from '../types';
import { useOnboardingStyles } from '../onboardingStyles';
import { useOnbColors } from '../onboardingColors';
import { StepVideo } from '../common';
import pageStyles from '../Onboarding.module.css';

// Column-flow order so the 2-column grid reads down each column
const WHY_EXAMPLES = [
  'Sales & Revenue',
  'Booked Calls',
  'Calendar Appointments',
  'Quiz Completions',
  'Form Submissions',
  'Membership Signups',
  'Custom Events',
];

export function Step5Webhooks({ data, update }: StepProps) {
  const { classes, cx } = useOnboardingStyles();
  const col = useOnbColors();
  const selected = data.webhooks;
  const isOn = (id: string) => selected.some((w) => w.id === id);

  const toggle = (id: string) => {
    if (isOn(id)) update({ webhooks: selected.filter((w) => w.id !== id) });
    else update({ webhooks: [...selected, { id, name: '' }] });
  };

  const selectedLabels = selected.map((w) => WEBHOOK_INDEX[w.id]?.label).filter(Boolean);

  return (
    <Box className={`${classes.inner} ${pageStyles.stepEnter}`} style={{ maxWidth: 960 }}>
      <div className={classes.mark}>
        <IconShoppingCart size={28} />
      </div>
      <h1 className={classes.title}>
        Track Revenue &amp; Events <span className={classes.optionalTag}>(Optional)</span>
      </h1>
      <p className={classes.subtitle}>
        Connect your shopping cart, payment processor, CRM, or other business systems to send important
        events directly to Visiopt using secure server-to-server tracking.
      </p>

      <StepVideo sublabel="Connect carts & payment processors" modalTitle="How to connect your revenue sources" />

      <div className={classes.fields}>
        {/* Why connect */}
        <div className={classes.qaBox}>
          <div className={classes.qaHead}>
            <span className={classes.qaDot}>
              <IconQuestionMark size={13} />
            </span>
            Why connect your systems?
          </div>
          <Text className={classes.qaText}>
            Server-to-server tracking allows your business systems to securely send important events
            directly to Visiopt.
          </Text>
          <div className={classes.qaSubhead}>Examples include:</div>
          <div className={classes.checkGrid}>
            {WHY_EXAMPLES.map((e) => (
              <div key={e} className={classes.qaItem} style={{ fontWeight: 400 }}>
                <IconCheck size={16} color={col.purpleText} style={{ flexShrink: 0 }} /> {e}
              </div>
            ))}
          </div>
          <Text className={classes.qaNote}>
            This provides more accurate reporting and allows Visiopt to optimize using the events that
            matter most to your business.
          </Text>
        </div>

        {/* Select integrations */}
        <Box mt={6} style={{ textAlign: 'center' }}>
          <div className={classes.centerHeading}>Select Your Integrations</div>
          <Text fz={13.5} c="dimmed" mt={4}>
            Choose one or more systems you want Visiopt to receive events from. Select all that apply.
            Every business is different — simply select the systems that apply to you.
          </Text>
        </Box>

        {WEBHOOK_GROUPS.map((group) => (
          <Box key={group.title}>
            <Text className={classes.groupTitle} style={{ textTransform: 'uppercase' }}>
              {group.title}
            </Text>
            <div className={classes.logoGrid}>
              {group.items.map((it) => {
                const on = isOn(it.value);
                return (
                  <button
                    key={it.value}
                    type="button"
                    className={cx(classes.logoCard, { [classes.logoCardActive]: on })}
                    onClick={() => toggle(it.value)}
                  >
                    {on && (
                      <span className={classes.logoCheck}>
                        <IconCheck size={12} />
                      </span>
                    )}
                    <img className={classes.logoImg} src={it.logo} alt={it.label} loading="lazy" />
                    <span className={classes.logoLabel}>{it.label}</span>
                  </button>
                );
              })}
            </div>
          </Box>
        ))}

        {/* Can't find your platform */}
        <Box mt={2} style={{ textAlign: 'left' }}>
          <Text fw={700} fz="sm" c={col.textPrimary}>
            Can't find your platform?
          </Text>
          <Text fz={13.5} c="dimmed">
            Select Custom Integration and we'll guide you through connecting any platform that supports
            webhooks.
          </Text>
        </Box>

        {/* Custom integration */}
        <Box>
          <Text className={classes.groupTitle} style={{ textTransform: 'uppercase' }}>
            Custom Integration
          </Text>
          <div className={classes.logoGrid}>
            <button
              type="button"
              className={cx(classes.logoCard, { [classes.logoCardActive]: isOn(CUSTOM_INTEGRATION.value) })}
              onClick={() => toggle(CUSTOM_INTEGRATION.value)}
            >
              {isOn(CUSTOM_INTEGRATION.value) && (
                <span className={classes.logoCheck}>
                  <IconCheck size={12} />
                </span>
              )}
              <span className={classes.customTile}>
                <IconLink size={20} />
              </span>
              <span className={classes.logoLabel}>Custom Integration</span>
            </button>
          </div>
        </Box>

        {/* Selected line */}
        {selectedLabels.length > 0 && (
          <div className={classes.selectedLine}>
            Selected:{' '}
            {selectedLabels.map((l, i) => (
              <React.Fragment key={l}>
                <span className={classes.selectedName}>{l}</span>
                {i < selectedLabels.length - 1 && <span style={{ color: col.purpleDeep }}> • </span>}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Good to know */}
        <div className={classes.cachingBox}>
          <div className={classes.noteHeading} style={{ color: col.amberText }}>
            ⓘ GOOD TO KNOW
          </div>
          <Text fz={13.5} c={col.amberText}>
            This step is optional. You can always connect additional systems later as your business
            grows or your tracking needs change.
          </Text>
        </div>

        {/* Need more help */}
        <Box style={{ textAlign: 'center', borderTop: `1px solid ${col.border}`, paddingTop: 16 }}>
          <Text fw={700} fz="sm" c={col.textPrimary}>
            Need more help?
          </Text>
          <Text fz={13.5} c="dimmed" mt={4}>
            Most integrations can be completed by following the guided setup. After onboarding, if you'd
            like additional documentation or see advanced features available, you can also visit our{' '}
            <Anchor href="https://visiopt.com/help-videos/connect-carts.html" target="_blank" fw={600} c={col.purple}>
              Cart &amp; Payment Processor Connection Guide
            </Anchor>
            .
          </Text>
          <Text fz={12.5} c="dimmed" mt={12}>
            Estimated setup time: approximately 2–5 minutes per integration.
          </Text>
        </Box>
      </div>
    </Box>
  );
}
